import { apiFetch, getAccessToken, API_PREFIX } from '@/lib/api-client';
import type {
  OperatorAdminItem,
  CreateOperatorRequest,
  UpdateOperatorRequest,
  AdminUserItem,
  CreateUserRequest,
  UpdateUserRequest,
  ApiKeyAdminItem,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  AuditLogItem,
  GroupAdminItem,
  GroupMemberItem,
  CreateGroupRequest,
  UpdateGroupRequest,
  UpdateGroupViewConfigRequest,
  EmailTemplateItem,
  UpdateEmailTemplateRequest,
  EmailQueueItem,
  AssignmentRoutingItem,
  CreateAssignmentRoutingRequest,
  UpdateAssignmentRoutingRequest,
  OrderingConfigDto,
  SubjectSearchHit,
  EraseContactResult,
} from '@uniticket/shared';

// ── Order Automation Config (Admin) ─────────────────────────

export async function fetchOrderingConfig() {
  return apiFetch<{ data: OrderingConfigDto }>('/admin/ordering-config');
}

export async function updateOrderingConfig(data: { mode?: OrderingConfigDto['mode']; timeoutSeconds?: number }) {
  return apiFetch<{ data: OrderingConfigDto }>('/admin/ordering-config', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// ── Operators (Admin) ───────────────────────────────────────

export async function fetchOperatorsFull() {
  return apiFetch<{ data: OperatorAdminItem[] }>('/admin/operators/full');
}

export async function createOperatorApi(data: CreateOperatorRequest) {
  return apiFetch<{ data: OperatorAdminItem }>('/admin/operators', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOperatorApi(id: string, data: UpdateOperatorRequest) {
  return apiFetch<{ data: OperatorAdminItem }>(`/admin/operators/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteOperatorApi(id: string) {
  return apiFetch<{ data: { deleted: boolean } }>(`/admin/operators/${id}`, {
    method: 'DELETE',
  });
}

// ── Users (Admin) ───────────────────────────────────────────

export async function fetchAdminUsers(params: Record<string, string> = {}) {
  const query = new URLSearchParams(params).toString();
  return apiFetch<{ data: AdminUserItem[]; meta: { total: number; limit: number; offset: number } }>(
    `/admin/users${query ? `?${query}` : ''}`,
  );
}

export async function createUserApi(data: CreateUserRequest) {
  return apiFetch<{ data: AdminUserItem }>('/admin/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateUserApi(id: string, data: UpdateUserRequest) {
  return apiFetch<{ data: AdminUserItem }>(`/admin/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function resetUserPasswordApi(id: string) {
  return apiFetch<{ message: string }>(`/admin/users/${id}/reset-password`, {
    method: 'POST',
  });
}

export async function reset2faApi(id: string) {
  return apiFetch<{ message: string }>(`/admin/users/${id}/reset-2fa`, {
    method: 'POST',
  });
}

export async function resendWelcomeApi(id: string) {
  return apiFetch<{ message: string }>(`/admin/users/${id}/resend-welcome`, {
    method: 'POST',
  });
}

export async function convertAuthTypeApi(id: string, authType: string) {
  return apiFetch<{ data: AdminUserItem }>(`/admin/users/${id}/convert-auth`, {
    method: 'POST',
    body: JSON.stringify({ authType }),
  });
}

export async function anonymiseUserApi(id: string) {
  return apiFetch<{ data: { userId: string; anonymised: boolean } }>(
    `/admin/users/${id}/anonymise`,
    { method: 'POST' },
  );
}

export async function deleteUserApi(id: string) {
  return apiFetch<{ data: { deleted: boolean } }>(`/admin/users/${id}`, {
    method: 'DELETE',
  });
}

export interface UserDeps {
  createdTickets: number;
  assignedTickets: number;
  ticketUpdates: number;
  attachments: number;
  apiKeys: number;
  watchers: number;
  notifications: number;
}

export async function fetchUserDeps(id: string) {
  return apiFetch<{ data: UserDeps }>(`/admin/users/${id}/deps`);
}

// ── API Keys (Admin) ────────────────────────────────────────

export async function fetchApiKeys(params: Record<string, string> = {}) {
  const query = new URLSearchParams(params).toString();
  return apiFetch<{ data: ApiKeyAdminItem[] }>(
    `/admin/api-keys${query ? `?${query}` : ''}`,
  );
}

export async function createApiKeyApi(data: CreateApiKeyRequest) {
  return apiFetch<{ data: CreateApiKeyResponse }>('/admin/api-keys', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function revokeApiKeyApi(id: string) {
  return apiFetch<{ data: ApiKeyAdminItem }>(`/admin/api-keys/${id}/revoke`, {
    method: 'POST',
  });
}

// ── Groups (Admin) ─────────────────────────────────────────

export async function fetchAdminGroups(params: Record<string, string> = {}) {
  const query = new URLSearchParams(params).toString();
  return apiFetch<{ data: GroupAdminItem[] }>(
    `/admin/groups${query ? `?${query}` : ''}`,
  );
}

export async function createGroupApi(data: CreateGroupRequest) {
  return apiFetch<{ data: GroupAdminItem }>('/admin/groups', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateGroupApi(id: string, data: UpdateGroupRequest) {
  return apiFetch<{ data: GroupAdminItem }>(`/admin/groups/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteGroupApi(id: string) {
  return apiFetch<{ data: { deleted: boolean } }>(`/admin/groups/${id}`, {
    method: 'DELETE',
  });
}

export async function fetchGroupMembers(id: string) {
  return apiFetch<{ data: GroupMemberItem[] }>(`/admin/groups/${id}/members`);
}

export async function addGroupMembers(id: string, userIds: string[]) {
  return apiFetch<{ data: { added: number } }>(`/admin/groups/${id}/members`, {
    method: 'POST',
    body: JSON.stringify({ userIds }),
  });
}

export async function removeGroupMember(id: string, userId: string) {
  return apiFetch<{ data: { removed: boolean } }>(`/admin/groups/${id}/members/${userId}`, {
    method: 'DELETE',
  });
}

export async function fetchGroupConfig(id: string) {
  return apiFetch<{ data: any }>(`/admin/groups/${id}/config`);
}

export async function updateGroupConfig(id: string, data: UpdateGroupViewConfigRequest) {
  return apiFetch<{ data: any }>(`/admin/groups/${id}/config`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// ── Group Notification Config (Admin) ───────────────────────

export async function fetchGroupNotificationConfig(id: string) {
  return apiFetch<{ data: Record<string, boolean> }>(`/admin/groups/${id}/notifications`);
}

export async function updateGroupNotificationConfig(id: string, prefs: Record<string, boolean>) {
  return apiFetch<{ data: Record<string, boolean> }>(`/admin/groups/${id}/notifications`, {
    method: 'PATCH',
    body: JSON.stringify(prefs),
  });
}

// ── Audit Log (Admin) ───────────────────────────────────────

export async function fetchAuditLog(params: Record<string, string> = {}) {
  const query = new URLSearchParams(params).toString();
  return apiFetch<{ data: AuditLogItem[]; meta: { total: number; limit: number; offset: number } }>(
    `/admin/audit-log${query ? `?${query}` : ''}`,
  );
}

export async function fetchAuditActions() {
  return apiFetch<{ data: string[] }>('/admin/audit-log/actions');
}

// ── Email Templates (Admin) ─────────────────────────────────

export async function fetchEmailTemplates() {
  return apiFetch<{ data: EmailTemplateItem[] }>('/admin/email-templates');
}

export async function updateEmailTemplateApi(id: string, data: UpdateEmailTemplateRequest) {
  return apiFetch<{ data: EmailTemplateItem }>(`/admin/email-templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function previewEmailTemplate(id: string) {
  return apiFetch<{ data: { subject: string; html: string } }>(`/admin/email-templates/${id}/preview`, {
    method: 'POST',
  });
}

export async function resetEmailTemplate(eventType: string, language: string) {
  return apiFetch<{ data: EmailTemplateItem }>(`/admin/email-templates/reset/${eventType}/${language}`, {
    method: 'POST',
  });
}

// ── Email Queue (Admin) ─────────────────────────────────────

export async function fetchEmailQueue(params: Record<string, string> = {}) {
  const query = new URLSearchParams(params).toString();
  return apiFetch<{ data: EmailQueueItem[]; meta: { total: number; limit: number; offset: number } }>(
    `/admin/email-queue${query ? `?${query}` : ''}`,
  );
}

export async function retryEmailNotification(id: string) {
  return apiFetch<{ data: { retried: boolean } }>(`/admin/email-queue/${id}/retry`, {
    method: 'POST',
  });
}

// ── Assignment Routing (Admin) ───────────────────────────────

export async function fetchAssignmentRoutings() {
  return apiFetch<{ data: AssignmentRoutingItem[] }>('/admin/assignment-routing');
}

export async function createAssignmentRoutingApi(data: CreateAssignmentRoutingRequest) {
  return apiFetch<{ data: AssignmentRoutingItem }>('/admin/assignment-routing', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAssignmentRoutingApi(id: string, data: UpdateAssignmentRoutingRequest) {
  return apiFetch<{ data: AssignmentRoutingItem }>(`/admin/assignment-routing/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAssignmentRoutingApi(id: string) {
  return apiFetch<{ message: string }>(`/admin/assignment-routing/${id}`, {
    method: 'DELETE',
  });
}

export async function exportAuditLogCsv(params: Record<string, string> = {}): Promise<Blob> {
  const query = new URLSearchParams(params).toString();
  const token = getAccessToken();
  const res = await fetch(`${API_PREFIX}/admin/audit-log/export${query ? `?${query}` : ''}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(body.message || 'Export failed');
  }
  return res.blob();
}

// ── Privacy / Data Subject Requests (Admin) ─────────────────

export async function searchDataSubject(params: { email?: string; phone?: string; name?: string }) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v) as [string, string][],
  ).toString();
  return apiFetch<{ data: SubjectSearchHit[]; meta: { total: number } }>(
    `/admin/privacy/subject-search?${query}`,
  );
}

export async function eraseTicketContactApi(ticketId: string) {
  return apiFetch<{ data: EraseContactResult }>(
    `/admin/privacy/tickets/${ticketId}/erase-contact`,
    { method: 'POST' },
  );
}
